import React, { FC, forwardRef } from 'react';
import { Paper, Box, CardProps } from '@mui/material';
import { CheckIcon } from '@/src/components/icon/stroke/CheckIcon';
import { PlusIcon } from '@/src/components/icon/stroke/PlusIcon';
import { MinusIcon } from '@/src/components/icon/stroke/MinusIcon';
import { updateFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

/**
 * Standard MUI Card extended with custom properties
 */
export const FlowCard: FC<CardProps> = forwardRef(
  (
    {
      children,
      isSelected,
      disableIcon = true,
      iconSlots,
      iconPlacement = 'top-end',
      iconProps,
      hoverIconProps,
      iconWrapperProps,
      contentWrapperProps,
      onClick,
      disabled,
      color,
      ...rest
    },
    ref,
  ) => {
    const { sx: wrapperSx, ...restWrapper } = contentWrapperProps || {};
    const { sx: iconWrapperSx, className: iconWrapperClassName, ...restIconWrapper } = iconWrapperProps || {};
    const { sx: iconSx, ...restIcon } = iconProps || {};
    const { sx: hoverIconSx, ...restHoverIcon } = hoverIconProps || {};
    const { icon, selectedIcon, hoverIcon, hoverSelectedIcon } = {
      icon: PlusIcon,
      selectedIcon: CheckIcon,
      hoverIcon: PlusIcon,
      hoverSelectedIcon: MinusIcon,
      ...(iconSlots || {}),
    };
    const IconComponent = !disabled && isSelected ? selectedIcon : icon;
    const HoverIconComponent = !disabled && isSelected ? hoverSelectedIcon : hoverIcon;

    return (
      <Paper color={color} onClick={disabled ? undefined : onClick} {...rest} ref={ref}>
        <Box sx={{ borderRadius: 'inherit', overflow: 'hidden', height: '100%', ...wrapperSx }} {...restWrapper}>
          {!disableIcon && (
            <Box
              sx={{
                // circle
                border: 1,
                borderColor: 'onSurface.os100',
                backgroundColor: 'background.primary',
                ...(color === 'secondary' && {
                  backgroundColor: 'surface.primary',
                }),
                borderRadius: '1000px',
                p: '5px',
                // reset IconButton style
                width: 'auto',
                height: 'auto',
                transition: 'none',
                // interactive and selected effect is controlled by card.theme
                // placement
                position: 'absolute',
                ...(iconPlacement.includes('top') ? { top: '16px' } : {}),
                ...(iconPlacement.includes('bottom') ? { bottom: '16px' } : {}),
                ...(iconPlacement.includes('start') ? { left: '16px' } : {}),
                ...(iconPlacement.includes('end') ? { right: '16px' } : {}),
                ...iconWrapperSx,
                // Box overwritten to button
                '&.MuiButtonBase-root': {
                  ...updateFocusRingStyle(1),
                  // remove smooth transition effect to match card behavior
                  '&:before': {
                    transition: 'none',
                  },
                  // same style is also applied in card.theme
                  ...getInteractiveStyle(
                    {
                      backgroundColor: 'background.primary',
                      ...(color === 'secondary' && {
                        backgroundColor: 'surface.primary',
                      }),
                      ...(isSelected && {
                        backgroundColor: 'brand.primary',
                        borderColor: 'brand.primary',
                        color: 'common.white',
                      }),
                      '& .MuiSvgIcon-root': {
                        '&:first-of-type': {
                          display: 'block',
                        },
                        '&:last-of-type': {
                          display: 'none',
                        },
                      },
                    },
                    {
                      backgroundColor: 'surface.primary',
                      ...(color === 'secondary' && {
                        backgroundColor: 'surface.secondary',
                      }),
                      ...(isSelected && {
                        backgroundColor: 'brandHover.primary',
                        borderColor: 'brandHover.primary',
                        color: 'common.white',
                      }),
                      // flip icon being shown
                      '& .MuiSvgIcon-root': {
                        '&:first-of-type': {
                          display: 'none',
                        },
                        '&:last-of-type': {
                          display: 'block',
                        },
                      },
                    },
                  ),
                },
              }}
              className={['FlowCard-Icon-root', iconWrapperClassName].filter(Boolean).join(' ')}
              {...restIconWrapper}
            >
              <IconComponent
                data-testid="Card.Icon"
                sx={{ display: 'block', fontSize: '20px', ...iconSx }}
                {...restIcon}
              />
              <HoverIconComponent
                data-testid="Card.HoverIcon"
                sx={{ display: 'none', fontSize: '20px', ...hoverIconSx }}
                {...restHoverIcon}
              />
            </Box>
          )}
          {children}
        </Box>
      </Paper>
    );
  },
);
